import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/shared/composables/use-fetch'
import { addItems, clearItems } from '@/modules/shared/utils/store'
import { EntityMap, InProgress, Entity, AllGroup, ManageEntities } from '../types'
import { capitalize } from 'lodash'
import { useFetch as useFetchv4 } from '@/modules/core/composables/useFetch'

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

export const useEntityStore = defineStore('admin/entityStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `${route.params.slug}/admin`)

  const items = ref<EntityMap>(new Map())
  const entities = computed(() => Array.from(items.value.values()))

  const fetchEntities = async () => {
    clearItems(items)

    const { data, error } = await useFetch(`/${baseUrl.value}/entities`)
      .get()
      .json<{ data: Entity[]; has_more: boolean }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
    }

    addItems(
      items,
      data.value.data.map((item) => ({ ...item, _key: item.entity_type })),
    )
  }

  // This is to load the spv or fund that has in-progress status
  const inProgressEntities = ref<InProgress[]>([])

  const columnDefinitions = computed(() => {
    const firstRow = inProgressEntities.value[0]
    if (!firstRow) return []

    return Object.entries(firstRow)
      .filter(([key, _]) => key.startsWith('column_'))
      .map(([columnKey, columnValue]) => {
        const index = columnKey.split('_')[1] // Extract "1" from "column_1"
        const completedKey = `completed_${index}`

        return {
          key: completedKey,
          name: String(columnValue || ''),
          type: 'string',
          is_visible: true,
          fixed: false,
        }
      })
  })

  const allGroup = ref<AllGroup[]>([])
  const manageEntities = ref<ManageEntities[]>([])

  const fetchAllGroup = async () => {
    allGroup.value = []
    allGroup.value = await useFetchv4(`/v4/${baseUrl.value}/all-group`).get<AllGroup[]>()
  }

  const fetchManageEntities = async (entity_type) => {
    manageEntities.value = []
    manageEntities.value = await useFetchv4(`/v4/${baseUrl.value}/managed-entities/${entity_type}`).get<
      ManageEntities[]
    >()
  }

  const fetchFundsInProgress = async () => {
    inProgressEntities.value = []
    inProgressEntities.value = await useFetchv4(`/v4/${baseUrl.value}/funds-in-progress`).get<InProgress[]>()
  }

  const fetchSpvsInProgress = async () => {
    inProgressEntities.value = []
    inProgressEntities.value = await useFetchv4(`/v4/${baseUrl.value}/spvs-in-progress`).get<InProgress[]>()
  }

  const fetchSpvsOnHold = async () => {
    inProgressEntities.value = []
    inProgressEntities.value = await useFetchv4(`/v4/${baseUrl.value}/spvs-on-hold`).get<InProgress[]>()
  }

  const fetchFundsInFormation = async () => {
    inProgressEntities.value = []
    inProgressEntities.value = await useFetchv4(`/v4/${baseUrl.value}/funds-in-formation`).get<InProgress[]>()
  }

  const toggleOnHold = async (entity: InProgress) => {
    try {
      await useFetchv4(`/v4/${entity.group_subdomain}/tasking/task-lists/${entity.task_list_id}/toggle-on-hold`).post()
      inProgressEntities.value = inProgressEntities.value.filter((e) => e.id !== entity.id)
    } catch {
      // no-op, entity stays in current list
    }
  }

  return {
    items,
    entities,
    inProgressEntities,
    allGroup,
    manageEntities,
    columnDefinitions,
    fetchFundsInProgress,
    fetchSpvsInProgress,
    fetchSpvsOnHold,
    fetchFundsInFormation,
    toggleOnHold,
    fetchEntities,
    fetchAllGroup,
    fetchManageEntities,
  }
})
